import React, {PropsWithChildren, useEffect} from 'react';
import {WithMsalProps} from '@azure/msal-react';
import EnumContext, {IEnum} from './enum-context';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import {EnumerationClient} from '../Api/EnumerationClient';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {
    BusStopReply,
    CommercialStatusReply, ContactTypeReply, CostClaimableReply, CouncilReply, CrewTypeReply, DfePartialReasonsReply, DfeStatusReply, DrawnByReply, ExchangeReply,
    ExternalInvoiceStatusReply, FeeTypesReply, FileFoldersReply, FileStatusReply, FileTypesReply, JobPriorityReply,
    JobStatusReply, JobTypeReply, NoteStatusReply,
    PermitStatusReply,
    QuoteStatusReply,
    SlaTypeReply, TmUsageTypeReply,
    WorkStreamReply
} from '../Api/ControllerTypes/EnumerationTypes';

type Props = WithMsalProps & {
    children: React.ReactNode;
};

const EnumProvider: React.FC<Props> = (props: PropsWithChildren<Props>) => {
    const [enums, setEnums] = React.useState<IEnum | null>(null);

    useEffect(() => {
        async function fetchValues() {
            const enumerationClient = new NSwagAdapter<EnumerationClient>(new EnumerationClient(
                new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                    new SafeDigAccessTokenProvider(props.msalContext))));

            //Read workstream data
            const workStreamResult = await enumerationClient.callWithReply(
                WorkStreamReply, (client) => client.getWorkStreams());
            workStreamResult.throwIfFailed();

            //Get SLA Types
            const slaResult = await enumerationClient.callWithReply(
                SlaTypeReply, (client) => client.getQuotationSlas());
            slaResult.throwIfFailed();

            //Get Quotation Statuses
            const quoteResult = await enumerationClient.callWithReply(
                QuoteStatusReply, (client) => client.getQuotationStatus(''));
            quoteResult.throwIfFailed();

            //Get Job Statuses
            const jobResult = await enumerationClient.callWithReply(
                JobStatusReply, (client) => client.getJobStatus(''));
            jobResult.throwIfFailed();

            //Get Permit Statuses
            const permitResult = await enumerationClient.callWithReply(
                PermitStatusReply, (client) => client.getPermitStatuses());
            permitResult.throwIfFailed();

            //Read commercial status data.
            const commercialResult = await enumerationClient.callWithReply(
                CommercialStatusReply, (client) => client.getCommercialStatuses());
            commercialResult.throwIfFailed();

            //Read external Invoices data.
            const extInvoiceResult = await enumerationClient.callWithReply(
                ExternalInvoiceStatusReply, (client) => client.getExternalInvoicesReceived());
            extInvoiceResult.throwIfFailed();

            //Read DFE Status data.
            const dfeStatusResult = await enumerationClient.callWithReply(
                DfeStatusReply, (client) => client.getDfeStatuses());
            dfeStatusResult.throwIfFailed();

            //Get Crew Types
            const crewTypeResult = await enumerationClient.callWithReply(
                CrewTypeReply, (client) => client.getCrewTypes());
            crewTypeResult.throwIfFailed();

            //Get Contact Types
            const contactTypeResult = await enumerationClient.callWithReply(
                ContactTypeReply, (client) => client.getContactTypes());
            contactTypeResult.throwIfFailed();

            //Get current exchange list
            const exchangeResult = await enumerationClient.callWithReply(
                ExchangeReply, (client) => client.getExchanges());
            exchangeResult.throwIfFailed();

            //Get Job Proirities
            const jobPrioritiesResult = await enumerationClient.callWithReply(
                JobPriorityReply, (client) => client.getJobPriority());
            jobPrioritiesResult.throwIfFailed();

            //Get Job Types
            const jobTypeResult = await enumerationClient.callWithReply(
                JobTypeReply, (client) => client.getJobType());
            jobTypeResult.throwIfFailed();

            //Get Cost Claimables
            const costResult = await enumerationClient.callWithReply(
                CostClaimableReply, (client) => client.getCostClaimables());
            costResult.throwIfFailed();

            //Get Bus Stop Types
            const busStopResult = await enumerationClient.callWithReply(
                BusStopReply, (client) => client.getBusStopValues());
            busStopResult.throwIfFailed();

            //Get DFE Partial Reasons
            const dfePartialReasons = await enumerationClient.callWithReply(
                DfePartialReasonsReply, (client) => client.getDfePartialReasons());
            dfePartialReasons.throwIfFailed();

            //Get Councils
            const councilResult = await enumerationClient.callWithReply(
                CouncilReply, (client) => client.getCouncils());
            councilResult.throwIfFailed();

            //Get Drawn By List
            const drawnByResult = await enumerationClient.callWithReply(
                DrawnByReply, (client) => client.getDrawnByValues());
            drawnByResult.throwIfFailed();

            //Get File states
            const fileStateResult = await enumerationClient.callWithReply(
                FileStatusReply, (client) => client.getFileStates());
            fileStateResult.throwIfFailed();

            //Get file types
            const fileTypeResult = await enumerationClient.callWithReply(
                FileTypesReply, (client) => client.getDocumentTypes());
            fileTypeResult.throwIfFailed();

            //Get folder types.
            const folderTypeResult = await enumerationClient.callWithReply(
                FileFoldersReply, (client) => client.getFileFolders());
            folderTypeResult.throwIfFailed();

            //Get TM Usage Types
            const tmUsageResult = await enumerationClient.callWithReply(
                TmUsageTypeReply, (client) => client.getTmUsageTypes());
            tmUsageResult.throwIfFailed();

            //Get note statuses
            const noteStateResult = await enumerationClient.callWithReply(
                NoteStatusReply, (client) => client.getNoteStates());
            noteStateResult.throwIfFailed();

            //Get fee types
            const feeTypesResult = await enumerationClient.callWithReply(
                FeeTypesReply, (client) => client.getFeeTypes());
            feeTypesResult.throwIfFailed();

            setEnums({
                exchangeList: exchangeResult.reply!.exchanges,
                workStreams: workStreamResult.reply!.workStreams,
                slaTypes: slaResult.reply!.slaTypes,
                quoteStatuses: quoteResult.reply!.quoteStatuses,
                jobPriority: jobPrioritiesResult.reply!.jobPriorities,
                jobTypes: jobTypeResult.reply!.jobTypes,
                jobStatuses: jobResult.reply!.jobStatuses,
                costTypes: costResult.reply!.costClaimables,
                busStopValues: busStopResult.reply!.busStopValues,
                permitStatuses: permitResult.reply!.permitStatuses,
                commercial: commercialResult.reply!.commercialStatuses,
                extInvoice: extInvoiceResult.reply!.externalInvoicesReceived,
                dfeStatuses: dfeStatusResult.reply!.dfeStatuses,
                dfePartialReasons: dfePartialReasons.reply!.dfePartialReasons,
                crewTypes: crewTypeResult.reply!.crewTypes,
                contactTypes: contactTypeResult.reply!.contactTypes,
                councilList: councilResult.reply!.councils,
                drawnByValues: drawnByResult.reply!.drawByValues,
                fileStatuses: fileStateResult.reply!.fileStatuses,
                fileTypes: fileTypeResult.reply!.documentTypes,
                fileFolders: folderTypeResult.reply!.fileFolders,
                tmUsage: tmUsageResult.reply!.tmUsageTypes,
                noteStatuses: noteStateResult.reply!.noteStatuses,
                feeTypes: feeTypesResult.reply!.feeTypes
            });
        }

        fetchValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = {
        enums,
        setEnums
    };

    return (
        <EnumContext.Provider value={value}>
            {props.children}
        </EnumContext.Provider>
    );
}

export default EnumProvider;
